import { useMemo, useState } from "react";
import {
  Calendar,
  DollarSign,
  GraduationCap,
  Search,
  Info,
  Stethoscope,
} from "lucide-react";
import { DoctorWithScheduleDto } from "../../../web-api-client";
import { getDoctorDefaultImg } from "../../../utils/defaultImage";
import { useLanguage } from "../../../i18n/LanguageContext";
import { useNavigate } from "react-router-dom";

export function DoctorListDesktop({
  doctors,
  onSelect,
  onSkip,
}: {
  doctors: DoctorWithScheduleDto[];
  onSelect: (d: DoctorWithScheduleDto) => void;
  onSkip: () => void;
}) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredDoctors = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return doctors;
    return doctors.filter((d) => (d.name ?? "").toLowerCase().includes(q));
  }, [doctors, searchTerm]);

  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleViewDetail = (doctor: DoctorWithScheduleDto) => {
    if (!doctor.doctorId && !doctor.id) return;

    const params = new URLSearchParams({
      doctorId: String(doctor.doctorId ?? doctor.id),
    });

    navigate(`/profile?${params.toString()}`);
  };

  return (
    <div className="right-column">
      <div className="mb-4">
        <div className="flex space-x-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Tìm nhanh bác sĩ"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-1 focus:ring-green-500 focus:border-transparent outline-none"
              style={{ paddingBottom: "0.6rem", paddingTop: "0.6rem" }}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          className="mb-4 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
          onClick={onSkip}
        >
          Bỏ qua
        </button>
      </div>

      <div className="space-y-4 op-0 animate-slide-up-delay-1">
        {filteredDoctors.map((doctor) => (
          <div
            key={doctor.id}
            className="relative border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow duration-200 cursor-pointer bg-white"
            onClick={() => onSelect(doctor)}
          >
            {/* Nút xem chi tiết góc phải trên */}
            <button
              type="button"
              aria-label="Xem thông tin chi tiết"
              title="Xem thông tin chi tiết"
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetail(doctor);
              }}
              className="
                absolute top-3 right-3
                rounded-full py-1 px-3
                border border-green-200 bg-white/95
                flex items-center justify-center
                text-green-600 shadow-sm
                hover:bg-green-50 hover:border-green-300 hover:text-green-700
                transition-all duration-200 text-sm
              "
            >
              <Info className="w-4 h-4" />
              <span className="ml-2 text-xs">Thông tin</span>
            </button>

            <div className="flex items-start space-x-4 pr-12">
              <img
                className={`w-20 ${doctor.image ? "h-24" : "24"} rounded-lg object-cover`}
                src={doctor.image ?? getDoctorDefaultImg(doctor.gender)}
                alt={doctor.name ?? ""}
                onError={(e) => {
                  const img = e.currentTarget as HTMLImageElement;
                  const fallback = getDoctorDefaultImg(doctor.gender);
                  if (!img.src.includes(fallback)) {
                    img.onerror = null;
                    img.src = fallback;
                  }
                }}
              />

              <div className="flex-1">
                <p className="text-base font-semibold text-orange-500 mb-2">
                  {doctor.name}
                </p>

                <div className="space-y-1 text-sm mb-2">
                  <div className="flex items-center space-x-2">
                    <GraduationCap className="w-5 h-5 text-primary" />
                    <span>{t("chucdanh")}:</span>
                    {doctor.title && doctor.title !== "" ? (
                      <span>{doctor.title}</span>
                    ) : (
                      <span className="text-gray-500">Đang cập nhật</span>
                    )}
                  </div>
                </div>

                {doctor.specialty && doctor.specialty.trim() !== "" && (
                  <div className="flex items-center text-sm space-x-2 mb-2">
                    <Stethoscope className="w-5 h-5 text-primary" />
                    <span>{t("chuyenkhoa")}:</span>
                    {doctor.specialty && doctor.specialty !== "" ? (
                      <span>{doctor.specialty}</span>
                    ) : (
                      <span className="text-gray-500">Đang cập nhật</span>
                    )}
                  </div>
                )}

                <div className="space-y-1 text-sm">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    <span>{t("lichkham")}:</span>
                    <span>
                      {doctor.daysThisWeek?.join(", ") || "Không có lịch khám"}
                    </span>
                  </div>
                </div>

                <div className="space-y-1 text-sm mt-2">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="w-5 h-5 text-primary" />
                    <span>{t("giakham")}</span>
                    <span className="text-green-600">
                      {(doctor.consultationFee ?? 1600000).toLocaleString(
                        "vi-VN",
                      )}
                      đ
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {searchTerm && filteredDoctors.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Không tìm thấy bác sĩ
          </h3>
          <p className="text-gray-600">Thử tìm kiếm với từ khóa khác</p>
        </div>
      )}
    </div>
  );
}
